/**
 * Хранение токенов (localStorage) и режима просмотра (sessionStorage — живёт до закрытия вкладки). Хранилище — только удобство:
 * все обращения в try/catch, при недоступности хранилища работаем из памяти.
 */

export interface TokenPair {
  access: string;
  refresh: string;
  expires_in: number;
}

/** Ключи хранилища по умолчанию — зоны курьера и диспетчерской (общий вход). */
export const DEFAULT_TOKENS_KEY = 'dalat.courier.tokens';
export const DEFAULT_VIEW_AS_KEY = 'dalat.courier.viewAs';

let tokensKey = DEFAULT_TOKENS_KEY;
let viewAsKey = DEFAULT_VIEW_AS_KEY;

let memTokens: TokenPair | null = null;
let memViewAs: string | null = null;
let loaded = false;
const listeners = new Set<() => void>();

type StoreKind = 'local' | 'session';

function storage(kind: StoreKind): Storage | undefined {
  return kind === 'local' ? globalThis.localStorage : globalThis.sessionStorage;
}

function readStorage(key: string, kind: StoreKind = 'local'): string | null {
  try {
    return storage(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null, kind: StoreKind = 'local'): void {
  try {
    if (value === null) storage(kind)?.removeItem(key);
    else storage(kind)?.setItem(key, value);
  } catch {
    /* хранилище недоступно — остаёмся в памяти */
  }
}

function ensureLoaded(): void {
  if (loaded) return;
  loaded = true;
  const raw = readStorage(tokensKey);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<TokenPair>;
      if (typeof parsed.access === 'string' && typeof parsed.refresh === 'string') {
        memTokens = { access: parsed.access, refresh: parsed.refresh, expires_in: parsed.expires_in ?? 0 };
      }
    } catch {
      memTokens = null;
    }
  }
  memViewAs = readStorage(viewAsKey, 'session');
}

function emit(): void {
  for (const l of listeners) l();
}

/**
 * Свои ключи хранилища для зоны (клиентское приложение — `dalat.client.tokens`): токены зон не пересекаются.
 * Вызывать до первого обращения к API зоны. Без вызова — ключи курьера/диспетчерской.
 */
export function configureTokenStorage(tokens: string, viewAs: string = `${tokens}.viewAs`): void {
  if (tokens === tokensKey && viewAs === viewAsKey) return;
  tokensKey = tokens;
  viewAsKey = viewAs;
  loaded = false;
  memTokens = null;
  memViewAs = null;
  emit();
}

export function getTokens(): TokenPair | null {
  ensureLoaded();
  return memTokens;
}

export function setTokens(tokens: TokenPair | null): void {
  ensureLoaded();
  memTokens = tokens;
  writeStorage(tokensKey, tokens ? JSON.stringify(tokens) : null);
  emit();
}

export function clearTokens(): void {
  setTokens(null);
}

/** `X-View-As`: id курьера, кабинет которого смотрит диспетчер/владелец (read-only на сервере; хранится в sessionStorage). */
export function getViewAs(): string | null {
  ensureLoaded();
  return memViewAs;
}

export function setViewAs(courierId: string | null): void {
  ensureLoaded();
  memViewAs = courierId;
  writeStorage(viewAsKey, courierId, 'session');
  emit();
}

/** Подписка на изменения токенов/режима просмотра (для useSyncExternalStore). */
export function subscribeAuth(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Только для тестов: сбросить кэш памяти и перечитать хранилище. */
export function __resetAuthForTests(): void {
  tokensKey = DEFAULT_TOKENS_KEY;
  viewAsKey = DEFAULT_VIEW_AS_KEY;
  loaded = false;
  memTokens = null;
  memViewAs = null;
}
